import { can, codeKind, formatCode, normalizeCode } from '@pl/domain';
import type { CountDto, CountListItemDto, CountProgressDto } from '@pl/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ApiError, api } from '../../lib/api';
import { cancelErrorText, countSubject, mayCancelAt, useCancelCount } from '../../lib/cancelCount';
import { qk, useMe, useSkuMap, useSkus } from '../../lib/queries';
import { skuMatches } from '../../lib/skuSearch';
import { Bar, Empty, ErrorNote, RefetchNote, SkuTag, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { RedScreen } from '../RedScreen';
import { announceRefusal } from '../refusalGuard';
import { feedback } from '../scanner/feedback';
import { ScannerView } from '../scanner/ScannerView';
import { useScanSite } from '../useScanSite';
import { useTodaysCounts } from '../../lib/todaysCounts';
import { enterCount, OPEN_POLL_MS, openCountState } from './countKit';
import { kit, ScanSheet } from './scanKit';
import s from './CountHome.module.css';

type Mode = 'today' | 'pick' | 'pallet';
type Start = { scope: 'sku'; skuId: string } | { scope: 'pallet'; palletCode: string };

interface Red {
  title: string;
  message: string;
  code?: string;
  kicker: string;
}

/** A count already open for what the worker tried to start: the one to join instead of a second. */
interface Join {
  countId: string;
  what: 'SKU' | 'pallet';
}

/** The open count's id, when a 409 names it. */
const openCountId = (err: unknown): string | null => {
  if (!(err instanceof ApiError) || err.code !== 'COUNT_IN_PROGRESS') return null;
  const id = (err.details as { countId?: unknown } | undefined)?.countId;
  return typeof id === 'string' ? id : null;
};

/** "8:40 AM", on the site's wall clock. */
const clock = (iso: string, timeZone: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone });

/**
 * Where a count starts: one of today's suggestions, any SKU at all, or a pallet by its sticker.
 *
 * Nothing on this screen says how many boxes anything should have — not the suggestions, not the
 * finished counts' expected numbers. A finished count shows only how many went missing, which is
 * the job it leaves a lead, never the number the next counter could aim at.
 */
export function CountHome() {
  const me = useMe();
  const { site } = useScanSite();
  const skuById = useSkuMap();
  const skus = useSkus();
  const qc = useQueryClient();
  const nav = useNavigate();
  // Every 10 s: a chip says how an earlier count went, and a count may open here unheard (I-1).
  const today = useTodaysCounts(site, { openPollMs: OPEN_POLL_MS });
  const searchId = useId();

  const [mode, setMode] = useState<Mode>('today');
  const [search, setSearch] = useState('');
  const [red, setRed] = useState<Red | null>(null);
  const [join, setJoin] = useState<Join | null>(null);
  // A lead's cancel, from the In progress list, and what the count screen said on its way back here.
  const [canceling, setCanceling] = useState<CountListItemDto | null>(null);
  const cancel = useCancelCount();
  const keepOpen = useRef<HTMLButtonElement>(null);
  const notice = (useLocation().state as { notice?: string } | null)?.notice;

  const showRed = useCallback((next: Red) => {
    announceRefusal();
    setRed(next);
  }, []);

  const start = useMutation({
    mutationFn: (what: Start) => api.post<CountDto>('/counts', { siteId: site!.id, ...what }),
    onMutate: () => setJoin(null),
    onSuccess: (dto) => {
      // The count screen's own read, from the answer: the full count is never cached on this side.
      const progress: CountProgressDto = {
        id: dto.id,
        siteId: dto.siteId,
        scope: dto.scope,
        skuId: dto.skuId,
        palletId: dto.palletId,
        palletCode: dto.palletCode,
        status: dto.status,
        startedAt: dto.startedAt,
        startedByName: dto.startedByName,
        scanned: dto.scanned,
        mine: true,
        cancelledBy: null,
      };
      qc.setQueryData(qk.countProgress(dto.id), progress);
      // Into every cached list of open counts at once, and no list from before now trusted again.
      enterCount(qc, dto.siteId, dto);
      // Marked stale, never fetched here: this screen is still mounted for this one moment, and a
      // refetch now is a request for finished results and suggestions at the instant the count
      // opens. They are asked for again when the worker comes back to this screen.
      void qc.invalidateQueries({ queryKey: ['counts'], refetchType: 'none' });
      void qc.invalidateQueries({ queryKey: ['counts-suggested'], refetchType: 'none' });
      // In one commit with the address, while the start is still pending (m-E). By default the router
      // draws the next screen later, in a transition, and in between this screen's pallet scanner came
      // back on: a box scanned then was refused as a box sticker, and lost.
      nav(`/scan/count/${dto.id}`, { flushSync: true });
    },
    onError: (err, what) => {
      const pretty = what.scope === 'pallet' ? formatCode(what.palletCode) : undefined;
      // Two counts of the same thing at once would split the boxes between them and call each
      // other's finds missing. The server names the one already open, and the worker joins it.
      if (err instanceof ApiError && err.code === 'COUNT_IN_PROGRESS') {
        const countId = openCountId(err);
        if (countId) {
          setJoin({ countId, what: what.scope === 'pallet' ? 'pallet' : 'SKU' });
          return;
        }
        feedback('bad');
        showRed({ title: 'Already being counted', message: err.message, code: pretty, kicker: 'Count not started' });
        return;
      }
      // A pallet the server will not count (another site's, never scanned in) is a refusal about
      // the sticker in the worker's hand, so it takes the screen like one.
      if (what.scope !== 'pallet') return;
      feedback('bad');
      showRed({ title: "Can't count this pallet", message: err.message, code: pretty, kicker: 'Count a pallet' });
    },
  });

  // A refusal from one way of starting says nothing about the next, so it does not follow the worker.
  const go = (next: Mode) => {
    start.reset();
    setJoin(null);
    setMode(next);
  };

  // Everything but a 409 the offer or the red screen already answers is shown beside the list.
  const plainError = start.error instanceof ApiError && start.error.code === 'COUNT_IN_PROGRESS' ? null : start.error;

  const offer = join && (
    <div className={s.joinOffer} role="status">
      <p className={s.joinText}>{`Someone is already counting this ${join.what}.`}</p>
      <Link to={`/scan/count/${join.countId}`} className={`bigbtn ${s.joinLink}`}>
        Join their count
      </Link>
    </div>
  );

  const redScreen = red && (
    <RedScreen title={red.title} message={red.message} code={red.code} kicker={red.kicker} note="Nothing was started." continueLabel="Got it" onContinue={() => setRed(null)} />
  );

  const onPalletCode = useCallback(
    (raw: string) => {
      const code = normalizeCode(raw);
      if (!code || codeKind(code) !== 'P') {
        feedback('bad');
        showRed({
          title: 'Scan the pallet sticker',
          message: code ? 'That is a box sticker. A pallet count starts from the sticker on the pallet itself.' : "This doesn't look like one of our stickers.",
          code: code ? formatCode(code) : raw.slice(0, 24),
          kicker: 'Count a pallet',
        });
        return;
      }
      start.mutate({ scope: 'pallet', palletCode: code });
    },
    [showRed, start],
  );

  const shown = useMemo(() => {
    const all = skus.data ?? [];
    // The Office picker's rule, so "cat6 blue" finds CAT6 CMR 1000ft Blue here too.
    return search.trim() ? all.filter((x) => skuMatches(x, search)) : all;
  }, [search, skus.data]);

  if (!site) {
    return (
      <div className="scan-body">
        <Empty title="No site on this account" description="Counts work from a site. Ask an admin to add you to one." />
      </div>
    );
  }

  if (mode === 'pick') {
    return (
      <>
        <div className={`scan-body ${s.pickBody}`}>
          <label className={s.pickLabel} htmlFor={searchId}>
            Search SKU or color
          </label>
          <div className={`inp lg ${s.pickSearch}`}>
            <Icon name="search" />
            <input id={searchId} className={s.pickInput} type="search" placeholder="Search SKU or color" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
          </div>
          <RefetchNote queries={[skus]} />
          {skus.isPending ? (
            <div className={kit.tkCentre}>
              <Spinner label="Loading SKUs" />
            </div>
          ) : skus.isLoadingError ? (
            <div className={kit.tkStack}>
              <ErrorNote error={skus.error} />
              <button type="button" className={`bigbtn ghost ${kit.tkRetry}`} onClick={() => void skus.refetch()}>
                Try again
              </button>
            </div>
          ) : shown.length === 0 ? (
            <p className={s.quiet}>No SKU matches that.</p>
          ) : (
            <div className="rows">
              {shown.map((x) => (
                <button key={x.id} type="button" className={`row ${s.skuBtn}`} disabled={start.isPending} onClick={() => start.mutate({ scope: 'sku', skuId: x.id })}>
                  <span className={`skux ${s.skuCell}`}>
                    <SkuTag sku={x} withCode />
                  </span>
                  <Icon name="chev-right" size={20} />
                </button>
              ))}
            </div>
          )}
          {offer}
          <ErrorNote error={plainError} />
        </div>
        <div className="scan-foot">
          <button type="button" className="bigbtn ghost" onClick={() => go('today')}>
            Back to today’s counts
          </button>
        </div>
        {redScreen}
      </>
    );
  }

  if (mode === 'pallet') {
    return (
      <>
        <div className={`scan-body pt ${s.palletBody}`}>
          <p className={s.palletLead}>
            <Icon name="pallet-load" size={22} />
            Scan the sticker on the pallet. Every box on it at {site.name} is what you count.
          </p>
          {/* Stopped while a red outcome holds the screen, so the next read cannot slip past unread. */}
          <ScannerView onCode={onPalletCode} enabled={red === null && !start.isPending} />
          {start.isPending && (
            <div className={kit.tkCentre}>
              <Spinner label="Starting the count" />
            </div>
          )}
          {offer}
        </div>
        <div className="scan-foot">
          <button type="button" className="bigbtn ghost" onClick={() => go('today')}>
            Back to today’s counts
          </button>
        </div>
        {redScreen}
      </>
    );
  }

  const { todo, done, open, total } = today;
  // Any count open here, or no word yet on whether one is: no earlier result, for anyone.
  const openHere = openCountState(today.opened, site.id);
  const loading = today.suggested.isPending || today.counts.isPending || today.opened.isPending;
  const failed = today.suggested.isLoadingError || today.counts.isLoadingError || today.opened.isLoadingError;
  // Leads open a finished result to act on it; for a worker the row only says it is done.
  const lead = Boolean(me.data && can(me.data.role, 'resolve_discrepancy'));
  const cancelError = cancelErrorText(cancel.error, site.name);
  // Only where this person may cancel: a lead's own sites, an admin's every site.
  const mayCancel = mayCancelAt(me.data, site.id);
  const closeCancel = () => {
    setCanceling(null);
    cancel.reset();
  };

  return (
    <>
      <div className={`scan-body ${s.homeBody}`}>
        {notice && (
          <p className={s.notice} role="status">
            {notice}
          </p>
        )}
        <RefetchNote queries={[today.suggested, today.counts, today.opened]} />
        {loading ? (
          <div className={kit.tkCentre}>
            <Spinner label="Loading today’s counts" />
          </div>
        ) : failed ? (
          <div className={kit.tkStack}>
            <ErrorNote error={today.suggested.error ?? today.counts.error ?? today.opened.error} />
            <button
              type="button"
              className={`bigbtn ghost ${kit.tkRetry}`}
              onClick={() => {
                void today.suggested.refetch();
                void today.counts.refetch();
                void today.opened.refetch();
              }}
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {total > 0 && (
              <div className={s.today}>
                <p className={s.todayLine}>{`${done.length} of ${total} done today`}</p>
                <Bar value={done.length} max={total} tone={done.length >= total ? 'ok' : 'accent'} />
              </div>
            )}

            {open.length > 0 && (
              <>
                <div className="sect">In progress</div>
                <ul className={s.countList} aria-label="In progress">
                  {open.map((c) => (
                    <li key={c.id} className={mayCancel ? s.openItem : undefined}>
                      <Link to={`/scan/count/${c.id}`} className={s.countRow}>
                        <span className={`skux ${s.skuCell}`}>
                          {c.scope === 'pallet' ? (
                            <span className={s.palletName}>
                              <Icon name="pallet-load" size={20} />
                              <b className="mono">{formatCode(c.palletCode ?? '')}</b>
                            </span>
                          ) : (
                            <SkuTag sku={skuById.get(c.skuId ?? '')} />
                          )}
                        </span>
                        {/* Who is counting, never how far: a tally read here, with the "N missing" the
                            count shows once finished, gives its expected. Its own tally is on its screen. */}
                        <span className={s.rowMeta}>{`In progress · ${c.startedByName}`}</span>
                      </Link>
                      {mayCancel && (
                        <button type="button" className={s.cancelBtn} aria-label={`Cancel count of ${countSubject(c, skuById)}`} onClick={() => setCanceling(c)}>
                          Cancel
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="sect">
              Today’s counts<span className="r">Never counted first</span>
            </div>
            {total === 0 ? (
              <Empty title="Nothing to count today" description={`No SKUs with stock at ${site.name} are due. Count any SKU or a pallet below.`} />
            ) : (
              <ul className={s.countList} aria-label="Today’s counts">
                {todo.map((skuId) => {
                  const sku = skuById.get(skuId);
                  return (
                    <li key={skuId}>
                      <button type="button" className={s.countRow} disabled={start.isPending} onClick={() => start.mutate({ scope: 'sku', skuId })}>
                        <span className={`skux ${s.skuCell}`}>
                          <SkuTag sku={sku} />
                        </span>
                        <span className={s.rowMeta}>
                          Not started{sku && <span className="mono">{` · ${sku.code}`}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
                {done.map((c) => {
                  const missing = c.result?.missing ?? 0;
                  // The server withheld this result (a count is open at the site): how it went is not
                  // known here, whatever the open list says.
                  const shown = openHere === 'none' && !c.resultHidden;
                  // While any count is open here, nothing of an earlier result — not its link, not
                  // how it went — and while the phone cannot tell, not even that plain "Done".
                  const inner = (
                    <>
                      <span className={`skux ${s.skuCell}`}>
                        <SkuTag sku={skuById.get(c.skuId ?? '')} />
                      </span>
                      <span className={s.rowMeta}>{`Done ${c.finishedAt ? clock(c.finishedAt, site.timezone) : ''} · ${c.startedByName}`}</span>
                      {shown ? (
                        <span className={`chip ${missing > 0 ? 'bad' : 'ok'} ${s.doneChip}`}>
                          {missing > 0 && <span className="dot" />}
                          {missing > 0 ? `Done · ${missing} missing` : 'Done · all found'}
                        </span>
                      ) : (
                        (openHere === 'open' || c.resultHidden) && <span className={`chip ${s.doneChip}`}>Done</span>
                      )}
                    </>
                  );
                  return (
                    <li key={c.id}>
                      {lead && shown ? (
                        <Link to={`/scan/count/${c.id}/result`} className={`${s.countRow} ${s.countDone}`}>
                          {inner}
                        </Link>
                      ) : (
                        <div className={`${s.countRow} ${s.countDone} ${s.countPlain}`}>{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {offer}
            <ErrorNote error={plainError} />
          </>
        )}
      </div>

      <div className="scan-foot">
        <button type="button" className="bigbtn ghost" onClick={() => go('pick')}>
          Count another SKU
        </button>
        <button type="button" className="bigbtn ghost" onClick={() => go('pallet')}>
          <Icon name="pallet-load" />
          Count a pallet
        </button>
      </div>
      {redScreen}
      <ScanSheet
        open={canceling !== null}
        onClose={closeCancel}
        busy={cancel.isPending}
        safeFocus={keepOpen}
        title={canceling ? `Cancel the count of ${countSubject(canceling, skuById)}?` : ''}
        lead="Nothing is recorded and no discrepancies are made."
        actions={
          <>
            {cancelError && (
              <p role="alert" className="error-note">
                {cancelError}
              </p>
            )}
            <button
              type="button"
              className={`bigbtn ${kit.tkDanger}`}
              disabled={cancel.isPending}
              onClick={() => canceling && cancel.mutate({ id: canceling.id }, { onSuccess: closeCancel })}
            >
              Cancel count
            </button>
            <button type="button" ref={keepOpen} className="bigbtn ghost" disabled={cancel.isPending} onClick={closeCancel}>
              Keep it open
            </button>
          </>
        }
      />
    </>
  );
}
